import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { FC, useContext, useMemo, useState } from "react";
import { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { clone } from "lodash";
import ManageSavedFilters from "@app/shared/manage-saved-filters/ManageSavedFilters";
import { FilterSet, FilterSetItem, FilterSetService } from "@app/resource-filters/filter-sets";
import { ResourceType } from "@app/resources";
import { FilterItemsGroupOperation } from "@app/resource-filters/common";
import MetOverallStatus from "@app/exceptions/metOverallStatusEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { Features } from "@app/feature-flags";
import UnprocessedExcludedProviders from "./unprocessedExcludedProviders";

const ExceptionsFilters: FC = () => {

  const filtersContext = useContext(ExceptionsFiltersContext);
  const featureFlags = useContext(FeatureFlagContext);
  const [filterValue, setFilterValue] = useState<string>("");
  const [loadedFilterSets, setLoadedFilterSets] = useState<FilterSet[]>([]);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

  const filterSetService = new FilterSetService();

  const getFilters = async () => {
    const filters = await filterSetService.getFilterSets(ResourceType.SpecialtyException)
    setLoadedFilterSets(filters);
    return filters.map(filter => filter.name);
  }

  const onFilterSetChange = (filterSelection: string) => {
    const selectedFilter = loadedFilterSets.find(filter => filter.name === filterSelection);
    setFilterValue(selectedFilter.name ?? "");
    filtersContext.setSelectedExceptionIds([]);
    filtersContext.setPageIndex(1);
    filtersContext.setResetToFirstPage(true);
    filtersContext.applyCurrentFilterSetItems(selectedFilter.filterGroups.flatMap(group => group.filters));
  }

  const onSubmitFilterSet = async (filtersToDelete: Set<string>, filterNameUpdates: Map<string, string>) => {
    for (const filterToDelete of filtersToDelete) {
      const matchingFilter = loadedFilterSets.find(filter => filter.name === filterToDelete);
      await filterSetService.delete(matchingFilter.filterSetId);
    }

    for (const [oldName, newName] of filterNameUpdates) {
      if (filtersToDelete.has(oldName) || newName === oldName || newName === "" || newName === "default") {
        return;
      }
      const oldFilter = loadedFilterSets.find(filter => filter.name === oldName);
      let filterSetItems: FilterSetItem[] = [];
      if (oldFilter) {
        await filterSetService.delete(oldFilter.filterSetId);
        filterSetItems = oldFilter.filterGroups.flatMap(group => group.filters);
      } else {
        filterSetItems = getCurrentFilterSetItems();
      }
      await filterSetService.create({
        filterGroups: oldFilter?.filterGroups ?? [{
          filters: filterSetItems,
          operation: FilterItemsGroupOperation.LogicalOr
        }],
        groupsOperation: oldFilter?.groupsOperation ?? FilterItemsGroupOperation.LogicalAnd,
        name: newName,
        targetResourceType: ResourceType.SpecialtyException,
      });
    }
  }

  const getCurrentFilterSetItems = (): FilterSetItem[] => {
    const filterSetItems = filtersContext.filterProjects.map(project => ({
      displayName: project.key,
      namespacePath: `Project§${project.key}`
    }))
      .concat(filtersContext.filterServiceArea.map(serviceArea => ({
        displayName: serviceArea,
        namespacePath: `ServiceArea§${serviceArea}`
      })))
      .concat(filtersContext.filterSpecialty.map(specialty => specialty.key)
        .filter((specialty, index, self) => self.indexOf(specialty) === index)
        .map(specialtyCode => ({
          displayName: specialtyCode,
          namespacePath: `Specialty§${specialtyCode}`
        })))
      .concat(filtersContext.filterExceptionStatus.map(exceptionStatus => ({
        displayName: exceptionStatus.key,
        namespacePath: `ExceptionStatus§${exceptionStatus.key}`
      })))
      .concat(filtersContext.filterServiceAreaStatuses.map(serviceAreaStatus => ({
        displayName: serviceAreaStatus.key ?? 'Blank',
        namespacePath: `ServiceAreaStatus§${serviceAreaStatus.key ?? 'Blank'}`
      })))
      .concat(filtersContext.filterGapResolutionStatuses.map(gapResolutionStatus => ({
        displayName: gapResolutionStatus.key,
        namespacePath: `GapResolutionStatus§${gapResolutionStatus.key}`
      })))
      .concat(filtersContext.filterLinesOfBusiness.map(line => ({
        displayName: line.key,
        namespacePath: `LineOfBusiness§${line.key}`
      })))
      .concat(filtersContext.filterExceptionGrades.map(exceptionGrade => ({
        displayName: exceptionGrade.key,
        namespacePath: `ExceptionGrade§${exceptionGrade.key}`
      })));
    filterSetItems.push({
      displayName: filtersContext.filterStartDate,
      namespacePath: `StartDate§${filtersContext.filterStartDate}`
    });
    filterSetItems.push({
      displayName: filtersContext.filterEndDate,
      namespacePath: `EndDate§${filtersContext.filterEndDate}`
    });
    filterSetItems.push({
      displayName: filtersContext.metOverallStatus,
      namespacePath: `MetOverallStatus§${filtersContext.metOverallStatus}`
    });
    filterSetItems.push({
      displayName: filtersContext.unprocessedExcludedProviders,
      namespacePath: `UnprocessedExcludedProviders§${filtersContext.unprocessedExcludedProviders}`
    });
    return filterSetItems;
  }

  const handleApplyFilters = () => {
    filtersContext.setSelectedExceptionIds([]);
    filtersContext.setPageIndex(1);
    filtersContext.setResetToFirstPage(true);
    filtersContext.applyFilters();
    setFiltersVisible(false);
  };

  const handleClearFilters = () => {
    setFilterValue("");
    filtersContext.setSelectedExceptionIds([]);
    filtersContext.setPageIndex(1);
    filtersContext.setResetToFirstPage(true);
    filtersContext.clearFilters();
  };

  const applyButton = (
    <QESButton
      disabled={filtersContext.applyDisabled}
      qesType="primary"
      qesTestId="exceptions__apply-filters"
      onClick={handleApplyFilters}
      className="col-auto col-xl-auto"
    >
      Apply
    </QESButton>
  );

  const clearButton = (
    <QESButton
      qesType="secondary"
      qesTestId="exceptions__clear-filters"
      onClick={handleClearFilters}
      className="col-auto col-xl-auto exceptions-filters__clear"
    >
      Clear All
    </QESButton>
  );

  const getSelectionText = (currentSelections: unknown[], totalOptions: unknown[]) => {
    const current = currentSelections.length;
    const total = totalOptions.length;

    if (total === 0 && !filtersContext.apiReadyFilters) {
      return "Loading";
    }
    return `${current.toLocaleString()} of ${total.toLocaleString()} item(s) selected`;
  }

  const serviceAreaSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterServiceArea, filtersContext.serviceAreaOptions.flatMap((sa) => sa.children));
  }, [filtersContext.filterServiceArea, filtersContext.serviceAreaOptions]);

  const serviceAreaStatusSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterServiceAreaStatuses, filtersContext.serviceAreaStatusOptions);
  }, [filtersContext.filterServiceAreaStatuses, filtersContext.serviceAreaStatusOptions]);

  const specialtySelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterSpecialty, filtersContext.specialtyOptions);
  }, [filtersContext.filterSpecialty, filtersContext.specialtyOptions]);

  const exceptionStatusSelectionText = useMemo(() => {
    let statusOptions = clone(filtersContext.exceptionStatusOptions)
    if (!filtersContext.isForArchived) {
      statusOptions = statusOptions.filter(op => op.key != "archived");
    }
    return getSelectionText(filtersContext.filterExceptionStatus, statusOptions);
  }, [filtersContext.filterExceptionStatus, filtersContext.exceptionStatusOptions]);

  const projectsSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterProjects, filtersContext.projectsOptions);
  }, [filtersContext.filterProjects, filtersContext.projectsOptions]);

  const linesOfBusinessSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterLinesOfBusiness, filtersContext.lineOfBusinessOptions);
  }, [filtersContext.filterLinesOfBusiness, filtersContext.lineOfBusinessOptions]);

  const gapResolutionStatusSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterGapResolutionStatuses, filtersContext.gapResolutionStatusOptions);
  }, [filtersContext.filterGapResolutionStatuses, filtersContext.gapResolutionStatusOptions]);

  const exceptionGradeSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterExceptionGrades, filtersContext.exceptionGradeOptions);
  }, [filtersContext.filterExceptionGrades, filtersContext.exceptionGradeOptions]);

  const sortedProjectsOptions = useMemo(() => {
    return filtersContext.projectsOptions.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filtersContext.projectsOptions]);

  const sortedLineOfBusinessOptions = useMemo(() => {
    return filtersContext.lineOfBusinessOptions.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filtersContext.lineOfBusinessOptions]);

  const sortedSpecialtyOptions = useMemo(() => {
    return filtersContext.specialtyOptions.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filtersContext.specialtyOptions]);

  const sortedExceptionStatusOptions = useMemo(() => {
    let statusOptions = clone(filtersContext.exceptionStatusOptions).slice().sort((a, b) => a.name.localeCompare(b.name));
    if (!filtersContext.isForArchived) {
      statusOptions = statusOptions.filter(op => op.key != "archived");
    }
    return statusOptions;
  }, [filtersContext.exceptionStatusOptions]);

  const sortedServiceAreaOptions = useMemo(() => {
    return filtersContext.serviceAreaOptions.map(state => ({
      ...state,
      children: state.children.slice().sort((a, b) => a.name.localeCompare(b.name))
    })).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filtersContext.serviceAreaOptions]);

  const sortedServiceAreaStatusOptions = useMemo(() => {
    return filtersContext.serviceAreaStatusOptions.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [filtersContext.serviceAreaStatusOptions]);

  const handleToggleFiltersMenu = () => {
    setFilterValue("");
    setFiltersVisible(!filtersVisible);
  }

  return (
    <>
      <div className="mb-3 mt-2 exceptions-filters">
        <QESButton
          qesType="secondary"
          qesTestId="exceptions-filters__toggle"
          onClick={handleToggleFiltersMenu}
          className="col-auto col-xl-auto exceptions-filters__toggle"
        >
          Filters
        </QESButton>
        {filtersVisible && (
          <>
            <div className="exceptions-filters__modal-screen"
                 onClick={handleToggleFiltersMenu}></div>
            <div className="exceptions-filters__container">
              <div className="exceptions-filters__manage">
                <ManageSavedFilters loadSavedFiltersList={getFilters} onFilterChange={onFilterSetChange}
                                    onSubmit={onSubmitFilterSet} filterValue={filterValue}/>
              </div>
              <div className="exceptions-filters__items">
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label exceptions-filters__header">Column</div>
                  <div className="exceptions-filters__input exceptions-filters__header">Value</div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Projects</div>
                  <div className="exceptions-filters__input">
                    <MultiselectDropdown
                      containerClassName="col-12 col-xl"
                      customWidth={true}
                      customSelectInputText={projectsSelectionText}
                      hideLabel={true}
                      items={sortedProjectsOptions}
                      label="Projects"
                      onSelectionChanged={(selectedItems) => filtersContext.setFilterProjects(selectedItems)}
                      selectedItems={filtersContext.filterProjects}
                      testId="exceptions-filters__projects"
                      showTooltipOnOverflow={true}
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Service Area - County SSA</div>
                  <div className="exceptions-filters__input">
                    <MultiselectNestedDropdown
                      containerClassName="col-12 col-xl"
                      customSelectInputText={serviceAreaSelectionText}
                      hideLabel={true}
                      items={sortedServiceAreaOptions}
                      label="Service Area - County SSA"
                      onSelectionChanged={(items) => filtersContext.setFilterServiceArea(items)}
                      selectedItems={filtersContext.filterServiceArea}
                      testId="exceptions-filters__service-area"
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Service Area Status</div>
                  <div className="exceptions-filters__input">
                    <MultiselectDropdown
                      containerClassName="col-12 col-xl"
                      customSelectInputText={serviceAreaStatusSelectionText}
                      hideLabel={true}
                      items={sortedServiceAreaStatusOptions}
                      label="Service Area Status"
                      onSelectionChanged={(items) => filtersContext.setFilterServiceAreaStatuses(items)}
                      selectedItems={filtersContext.filterServiceAreaStatuses}
                      testId="exceptions-filters__service-area-status"
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Specialty</div>
                  <div className="exceptions-filters__input">
                    <MultiselectDropdown
                      containerClassName="col-12 col-xl"
                      customWidth={true}
                      customSelectInputText={specialtySelectionText}
                      hideLabel={true}
                      items={sortedSpecialtyOptions}
                      label="Specialty"
                      onSelectionChanged={(selectedItems) => filtersContext.setFilterSpecialty(selectedItems)}
                      selectedItems={filtersContext.filterSpecialty}
                      testId="exceptions-filters__specialty"
                      showTooltipOnOverflow={true}
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Line of Business</div>
                  <div className="exceptions-filters__input">
                    <MultiselectDropdown
                      containerClassName="col-12 col-xl"
                      customWidth={true}
                      customSelectInputText={linesOfBusinessSelectionText}
                      hideLabel={true}
                      items={sortedLineOfBusinessOptions}
                      label="Line of Business"
                      onSelectionChanged={(selectedItems) => filtersContext.setFilterLinesOfBusiness(selectedItems)}
                      selectedItems={filtersContext.filterLinesOfBusiness}
                      testId="exceptions-filters__line-of-business"
                      showTooltipOnOverflow={true}
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Start Date</div>
                  <div className="exceptions-filters__input">
                    <QESDateInput
                      containerClassName="col-12 col-xl"
                      errorText="Start Date must be before Expiration Date"
                      hideLabel={true}
                      isValid={filtersContext.validStartDate}
                      label="Start Date"
                      testId="exceptions-filters__start-date"
                      onChange={(e) => filtersContext.setFilterStartDate(e.target.value)}
                      unrequiredValidation={true}
                      value={filtersContext.filterStartDate}
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Expiration Date</div>
                  <div className="exceptions-filters__input">
                    <QESDateInput
                      containerClassName="col-12 col-xl"
                      errorText="Expiration Date must be after Start Date"
                      hideLabel={true}
                      isValid={filtersContext.validEndDate}
                      label="Expiration Date"
                      testId="exceptions-filters__end-date"
                      onChange={(e) => filtersContext.setFilterEndDate(e.target.value)}
                      unrequiredValidation={true}
                      value={filtersContext.filterEndDate}
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Unprocessed Excluded Providers</div>
                  <div className="exceptions-filters__input">
                    <QESSelect
                      className="mb-0"
                      containerClassName="mb-0 col-12 col-xl"
                      hideLabel={true}
                      onChange={e => filtersContext.setUnprocessedExcludedProviders(e.target.value as UnprocessedExcludedProviders)}
                      testId="exceptions-filters__unprocessed-excluded-providers"
                      value={filtersContext.unprocessedExcludedProviders}
                      label=""
                    >
                      {Object.values(UnprocessedExcludedProviders).map(value => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
                {!filtersContext.isForArchived && (
                  <div className="exceptions-filters__item">
                    <div className="exceptions-filters__label">Exception Status</div>
                    <div className="exceptions-filters__input">
                      <MultiselectDropdown
                        containerClassName="col-12 col-xl"
                        customWidth={true}
                        customSelectInputText={exceptionStatusSelectionText}
                        hideLabel={true}
                        items={sortedExceptionStatusOptions}
                        label="Exception Status"
                        onSelectionChanged={(selectedItems) => filtersContext.setFilterExceptionStatus(selectedItems)}
                        selectedItems={filtersContext.filterExceptionStatus}
                        testId="exceptions-filters__county-status"
                      />
                    </div>
                  </div>
                )}
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Exception Grade</div>
                  <div className="exceptions-filters__input">
                    <MultiselectDropdown
                      containerClassName="col-12 col-xl"
                      customWidth={true}
                      customSelectInputText={exceptionGradeSelectionText}
                      hideLabel={true}
                      items={filtersContext.exceptionGradeOptions}
                      label="Exception Grade"
                      onSelectionChanged={(selectedItems) => filtersContext.setFilterExceptionGrades(selectedItems)}
                      selectedItems={filtersContext.filterExceptionGrades}
                      testId="exceptions-filters__exception-grade"
                    />
                  </div>
                </div>
                <div className="exceptions-filters__item">
                  <div className="exceptions-filters__label">Met Overall Status</div>
                  <div className="exceptions-filters__input">
                    <QESSelect
                      className="mb-0"
                      containerClassName="mb-0 col-12 col-xl"
                      hideLabel={true}
                      onChange={e => filtersContext.setMetOverallStatus(e.target.value as MetOverallStatus)}
                      testId="exceptions-filters__met-overall-status"
                      value={filtersContext.metOverallStatus}
                      label=""
                    >
                      {Object.values(MetOverallStatus).map(value => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
                {featureFlags.enabledFeatureFlags.has(Features.GAP_FILL_STATUS) && (
                  <div className="exceptions-filters__item">
                    <div className="exceptions-filters__label">Gap Resolution Status</div>
                    <div className="exceptions-filters__input">
                      <MultiselectDropdown
                        containerClassName="col-12 col-xl"
                        customWidth={true}
                        customSelectInputText={gapResolutionStatusSelectionText}
                        hideLabel={true}
                        items={filtersContext.gapResolutionStatusOptions}
                        label="Gap Resolution Status"
                        onSelectionChanged={(selectedItems) => filtersContext.setFilterGapResolutionStatuses(selectedItems)}
                        selectedItems={filtersContext.filterGapResolutionStatuses}
                        testId="exceptions-filters__gap-resolution-status"
                        showTooltipOnOverflow={true}
                      />
                    </div>
                  </div>
                )}
                <div className="d-flex col-12 col-xl exceptions__filter-buttons">
                  {clearButton}
                  {applyButton}
                </div>
              </div>
            </div>
          </>)
        }
      </div>
    </>
  );
}

export default ExceptionsFilters;
